/**
 * Iter 57 — Mobile Attendance (employee self-punch).
 *
 * Flow:
 *   1. Pick location — Office or Other (single plant, no project scoping)
 *   2. Face capture → descriptor
 *   3. GPS capture
 *   4. POST /api/mobile/attendance/punch
 *   5. Success / error sheet
 *
 * Designed mobile-first; safe to install as PWA.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MapPin, Briefcase, Plane, Clock, Check, LogIn, LogOut, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FaceCaptureView from '../../components/FaceCaptureView';
import { getCurrentPosition, loadFaceModels } from '../../lib/faceRecognition';
import { apiGet, apiPost } from '../../lib/api';
import { formatISTTime } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';

export default function MyAttendance() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [offices, setOffices] = useState([]);
  // location_type: 'office' | 'other'
  const [locType, setLocType] = useState(localStorage.getItem('att.loc_type') || 'office');
  const [officeId, setOfficeId] = useState(localStorage.getItem('att.last_office') || '');
  const [otherRemark, setOtherRemark] = useState('');
  const [enrolled, setEnrolled] = useState(null);
  const [todayPunches, setTodayPunches] = useState([]);
  const [kind, setKind] = useState('in');
  const [busy, setBusy] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [pendingPunch, setPendingPunch] = useState(null);
  // GPS pre-fetch ref — started on mount AND when camera opens so the position
  // is resolved (or very close to it) by the time face capture finishes.
  const gpsPromiseRef = useRef(null);
  // Submission guard — prevents a double-punch when auto-capture and the manual
  // button both fire in the same render cycle (before React disables the button).
  const submittingRef = useRef(false);

  useEffect(() => { if (officeId) localStorage.setItem('att.last_office', officeId); }, [officeId]);
  useEffect(() => { localStorage.setItem('att.loc_type', locType); }, [locType]);

  const isLocationValid = () => {
    if (locType === 'office') return !!officeId;
    if (locType === 'other') return otherRemark.trim().length >= 50;
    return false;
  };

  const sendPunch = async ({ descriptor, photoDataUrl, gps, off_radius_remark, liveness }) => {
    setBusy(true);
    try {
      const r = await apiPost('/mobile/attendance/punch', {
        descriptor, lat: gps.lat, lng: gps.lng,
        accuracy_m: gps.accuracy,              // GPS accuracy bypass for Xiaomi/Tecno/indoor
        location_type: locType,
        office_id: locType === 'office' ? officeId : null,
        other_remark: locType === 'other' ? otherRemark.trim() : null,
        kind,
        photo_url: photoDataUrl, device_id: 'pwa',
        off_radius_remark: off_radius_remark || null,
        liveness_signals: liveness || undefined,
        is_mock_location: !!gps.is_mock_location, // Fix 6 — propagate native flag
      });
      setOutcome({ status: r.status === 'pending_approval' ? 'pending_approval' : 'success', ...r, photoDataUrl, kind });
      setShowCamera(false);
      setPendingPunch(null);
      const refresh = await apiGet('/mobile/attendance/me/today');
      setTodayPunches(refresh.punches || []);
      setKind(kind === 'in' ? 'out' : 'in');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'off_radius_remark_required' && !off_radius_remark) {
        setPendingPunch({ descriptor, photoDataUrl, gps, liveness, distance_m: detail.distance_m, radius_m: detail.radius_m });
        setShowCamera(false);
      } else {
        setOutcome({
          status: 'error',
          message: (typeof detail === 'string' ? detail : detail?.message) || 'Punch failed',
          suggestReenroll: detail?.suggest_reenroll || false,
          consecutiveFails: detail?.consecutive_fails || 0,
        });
      }
    } finally { setBusy(false); }
  };

  const submit = async ({ descriptor, photoDataUrl, liveness }) => {
    // Dedupe: auto-capture + manual button can both fire before React disables
    // the button. The ref check is synchronous — the second caller exits immediately.
    if (submittingRef.current) return;
    submittingRef.current = true;
    let gps;
    try {
      // Use the GPS promise that was fired when the camera opened (or on mount).
      // By the time face capture completes (~2-3s) GPS is typically already
      // resolved, making this await nearly instant instead of 3-8 seconds.
      gps = await (gpsPromiseRef.current || getCurrentPosition());
    } catch (err) {
      submittingRef.current = false;
      if (err && err.message === 'MOCK_LOCATION_DETECTED') {
        toast.error(
          'Fake GPS detected. Please disable any location-spoofing apps and try again.',
          { duration: 8000 },
        );
        return;
      }
      toast.error('Could not read location — check device GPS settings.');
      return;
    }
    if (!gps) { submittingRef.current = false; toast.error('Location permission required'); return; }
    await sendPunch({ descriptor, photoDataUrl, gps, liveness });
    submittingRef.current = false;
  };

  useEffect(() => {
    // Pre-warm face-api models the moment the user lands on this page,
    // so by the time they tap "Start Punch" the heavy NN weights are already
    // resident in memory — cuts perceived launch latency from ~3s to ~300ms.
    loadFaceModels().catch(() => {});
    // Pre-warm GPS in parallel with model loading. On most devices this fires
    // the OS location request immediately; by the time the user taps "Start
    // Punch" and the face capture completes (~2-3s), GPS is already resolved.
    gpsPromiseRef.current = getCurrentPosition().catch(() => null);
    apiGet('/mobile/attendance/office-locations').then((r) => {
      setOffices(r?.items || []);
    }).catch(() => {});
    apiGet('/mobile/attendance/me/today').then((r) => {
      setTodayPunches(r.punches || []);
      const lastKind = (r.punches || []).slice(-1)[0]?.kind;
      if (lastKind === 'in') setKind('out');
    }).catch(() => {});
    // Resolve enrollment via /hr/me (email-canonical) instead of stale user.employee_id
    apiGet('/hr/me').then((r) => {
      const eid = r?.employee?.id;
      if (eid) {
        apiGet(`/mobile/attendance/enrollment/employee/${eid}`)
          .then((res) => setEnrolled(!!res.enrolled))
          .catch(() => setEnrolled(false));
      } else {
        setEnrolled(false);
      }
    }).catch(() => setEnrolled(false));
    // eslint-disable-next-line
  }, []);


  // Not enrolled CTA
  if (enrolled === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1e2a4d] to-[#0f1a2e] p-5 flex flex-col items-center justify-center text-white">
        <button onClick={() => navigate(-1)} className="absolute top-5 left-5 text-white/70 hover:text-white flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider" data-testid="att-not-enrolled-back">
          ← Back
        </button>
        <div className="max-w-sm w-full bg-white/10 backdrop-blur border border-white/15 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center mb-3 text-2xl">⚠️</div>
          <h2 className="font-display font-bold text-xl mb-1">Face not enrolled</h2>
          <p className="text-sm text-white/70 mb-4">You need a one-time face enrolment before you can punch attendance.</p>
          <button onClick={() => navigate('/m/enroll')} className="w-full bg-white text-slate-900 py-3 rounded-full font-display font-bold" data-testid="att-go-enroll-btn">
            Enrol my face →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1e2a4d] to-[#0f1a2e] text-white">
      {/* Header */}
      <header className="px-5 pt-7 pb-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-display font-bold">Site Attendance</div>
          <div className="font-display font-bold text-2xl mt-0.5">{user?.name || 'Welcome'}</div>
        </div>
        <button onClick={() => navigate('/app')} className="text-xs text-white/60 hover:text-white">← ERP</button>
      </header>

      {!showCamera && !outcome && (
        <main className="px-5 pb-24 space-y-4">
          {/* Location picker — Project / Office / Other (mandatory) */}
          <Card>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">Where are you punching from?</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <LocChip active={locType === 'office'} icon={Briefcase} label="Office" onClick={() => setLocType('office')} testid="att-loc-office" />
              <LocChip active={locType === 'other'} icon={Plane} label="Other" onClick={() => setLocType('other')} testid="att-loc-other" />
            </div>
            {locType === 'office' && (
              <div className="flex items-center gap-3">
                <Briefcase size={16} className="text-white/40 shrink-0" />
                <select
                  value={officeId}
                  onChange={(e) => setOfficeId(e.target.value)}
                  className="flex-1 bg-transparent text-white font-display font-semibold text-base focus:outline-none truncate"
                  data-testid="att-office-picker"
                >
                  <option value="" className="text-slate-900">{offices.length ? 'Pick an office…' : 'No offices configured · ask HR'}</option>
                  {offices.map((o) => <option key={o.id} value={o.id} className="text-slate-900">{o.name}</option>)}
                </select>
                <ChevronRight size={16} className="text-white/30" />
              </div>
            )}
            {locType === 'other' && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Plane size={14} className="text-white/40" />
                  <span className="text-[11px] text-white/60">Travelling / client meeting / off-site</span>
                </div>
                <textarea
                  value={otherRemark}
                  onChange={(e) => setOtherRemark(e.target.value)}
                  rows={2}
                  maxLength={240}
                  placeholder="Where are you & why? e.g. 'Client meet — Adani HQ Mumbai'"
                  className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
                  data-testid="att-other-remark"
                />
                <div className="text-[10px] text-white/40 text-right mt-0.5">{otherRemark.length}/240 · min 50 chars</div>
              </div>
            )}
          </Card>

          {/* Punch kind toggle */}
          <Card>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">Action</div>
            <div className="grid grid-cols-2 gap-2">
              <KindButton active={kind === 'in'} onClick={() => setKind('in')} icon={LogIn} label="Punch In" testid="att-kind-in" />
              <KindButton active={kind === 'out'} onClick={() => setKind('out')} icon={LogOut} label="Punch Out" testid="att-kind-out" />
            </div>
          </Card>

          {/* Today timeline */}
          {todayPunches.length > 0 && (
            <Card>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-1.5">
                <Clock size={12} /> Today's punches
              </div>
              <ul className="space-y-1.5">
                {todayPunches.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm">
                    {p.kind === 'in' ? <LogIn size={14} className="text-emerald-400" /> : <LogOut size={14} className="text-rose-400" />}
                    <span className="font-display font-semibold uppercase tracking-wider text-xs">{p.kind}</span>
                    <span className="text-white/60 text-xs ml-auto">{formatISTTime(p.ts)} <span className="opacity-50">IST</span></span>
                    {p.status === 'pending_approval' && <span className="text-amber-300 text-[10px] font-semibold bg-amber-400/20 px-1.5 py-0.5 rounded">Pending Approval</span>}
                    {p.in_geofence === false && p.status !== 'pending_approval' && <span className="text-amber-400 text-[10px]">⚠ off-site</span>}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* CTA */}
          <button
            onClick={() => {
              if (!isLocationValid()) {
                const msg = locType === 'office' ? (offices.length ? 'Pick an office' : 'No offices configured — ask HR/Admin to add one, or choose Other')
                  : 'Describe where you are (min 50 chars)';
                toast.error(msg);
                return;
              }
              // Re-fire GPS in parallel with the camera opening. The 60s maximumAge
              // means the browser returns the pre-warmed result instantly if it's
              // still fresh; otherwise a fresh acquisition races the face capture.
              gpsPromiseRef.current = getCurrentPosition().catch(() => null);
              setShowCamera(true);
            }}
            className="w-full bg-white text-slate-900 font-display font-bold text-base py-5 rounded-full shadow-2xl active:scale-[0.98] flex items-center justify-center gap-2"
            data-testid="att-start-punch-btn"
          >
            <MapPin size={18} /> Start {kind === 'in' ? 'Punch In' : 'Punch Out'}
          </button>
          <p className="text-[10px] text-center text-white/40">Powered by face recognition · works offline after first load.</p>
        </main>
      )}

      {showCamera && !outcome && (
        <main className="px-5 pb-12">
          <FaceCaptureView onCapture={submit} busy={busy} captureLabel={kind === 'in' ? 'Punch In' : 'Punch Out'} />
          <button onClick={() => setShowCamera(false)} className="mt-3 w-full text-center text-xs text-white/50 py-2">Cancel</button>
        </main>
      )}

      {outcome && (
        <OutcomeSheet
          outcome={outcome}
          onClose={() => setOutcome(null)}
          onAnother={() => { setOutcome(null); setShowCamera(false); }}
          onReEnrol={() => { setOutcome(null); navigate('/m/enroll?force=1'); }}
        />
      )}

      {pendingPunch && (
        <OffRadiusRemarkSheet
          pending={pendingPunch}
          busy={busy}
          onCancel={() => setPendingPunch(null)}
          onConfirm={(remark) => sendPunch({ ...pendingPunch, off_radius_remark: remark })}
        />
      )}
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white/10 backdrop-blur border border-white/15 rounded-2xl p-4">{children}</div>;
}

function LocChip({ active, icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`py-2 rounded-lg flex items-center justify-center gap-1.5 font-display font-semibold text-xs transition-colors ${
        active ? 'bg-white text-slate-900' : 'bg-white/10 text-white/70 border border-white/15'
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function KindButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`py-3 rounded-xl flex items-center justify-center gap-2 font-display font-semibold text-sm transition-colors ${
        active ? 'bg-white text-slate-900' : 'bg-white/10 text-white/80 border border-white/15'
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );
}

function OutcomeSheet({ outcome, onClose, onAnother, onReEnrol }) {
  const ok = outcome.status === 'success';
  const isPending = outcome.status === 'pending_approval';
  // Detect face-mismatch failures so we can surface a clear "Re-enrol" CTA.
  const msg = (outcome.message || '').toLowerCase();
  const isFaceMismatch = !ok && !isPending && (msg.includes('face mismatch') || msg.includes('face match') || msg.includes('not yet enrolled'));
  const suggestReenroll = outcome.suggestReenroll || false;
  const consecutiveFails = outcome.consecutiveFails || 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur flex items-end" data-testid="att-outcome">
      <div className={`w-full bg-white text-slate-900 rounded-t-3xl px-6 pt-7 pb-9 ${ok ? '' : isPending ? 'border-t-4 border-amber-400' : 'border-t-4 border-rose-500'}`}>
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${ok ? 'bg-emerald-100 text-emerald-700' : isPending ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
          {ok ? <Check size={32} /> : isPending ? <Clock size={28} /> : '⚠️'}
        </div>
        <h3 className="text-center font-display font-bold text-xl">
          {ok ? `Punched ${outcome.kind?.toUpperCase()}` : isPending ? 'Sent for Approval' : 'Punch Failed'}
        </h3>
        <p className="text-center text-sm text-slate-600 mt-1">
          {ok
            ? `${outcome.entity_name} · ${formatISTTime(outcome.ts)} IST`
            : isPending
            ? (outcome.message || 'Your punch from Other location is pending manager/HR approval. You will be marked Present once approved.')
            : outcome.message}
        </p>
        {ok && (
          <div className="mt-4 grid grid-cols-2 gap-2 text-center">
            <Stat label="Match" value={`${Math.round((1 - outcome.match_score) * 100)}%`} />
            <Stat label="Distance" value={`${outcome.distance_m ?? '—'} m`} />
          </div>
        )}
        {/* Urgent re-enrol banner — shown after 3+ consecutive failures */}
        {suggestReenroll && onReEnrol && (
          <div className="mt-4 bg-rose-50 border-2 border-rose-300 rounded-xl p-4" data-testid="att-reenroll-urgent">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-rose-600 text-base">⚠️</span>
              <span className="font-display font-bold text-sm text-rose-800">
                {consecutiveFails} consecutive failures
              </span>
            </div>
            <p className="text-xs text-rose-700 mb-3">
              Your face data may be outdated (lighting change, glasses, beard, etc.).
              Re-enrolment takes under 30 seconds — do it once and punching will work again.
            </p>
            <button
              onClick={onReEnrol}
              className="w-full bg-rose-600 text-white py-3 rounded-full font-display font-bold text-sm"
              data-testid="att-outcome-reenrol-urgent"
            >
              Re-enrol Face Now →
            </button>
          </div>
        )}
        {/* Soft re-enrol hint for regular face mismatch */}
        {!suggestReenroll && isFaceMismatch && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <strong>Tip:</strong> If you keep getting this even in good light, your face enrolment may be outdated.
            Tap <strong>Re-enrol Face</strong> below to redo it once — takes 5 seconds.
          </div>
        )}
        <div className="mt-5 flex gap-2">
          {(isFaceMismatch || suggestReenroll) && onReEnrol && !suggestReenroll && (
            <button onClick={onReEnrol} className="flex-1 bg-[#1F7A6E] text-white py-3 rounded-full font-display font-bold text-sm" data-testid="att-outcome-reenrol">Re-enrol Face</button>
          )}
          <button onClick={onAnother} className="flex-1 bg-white border-2 border-slate-200 py-3 rounded-full font-display font-bold text-sm" data-testid="att-outcome-another">Another</button>
          <button onClick={onClose} className="flex-1 bg-slate-900 text-white py-3 rounded-full font-display font-bold text-sm" data-testid="att-outcome-close">Done</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl py-2.5">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="font-display font-bold text-lg text-slate-900">{value}</div>
    </div>
  );
}


function OffRadiusRemarkSheet({ pending, busy, onCancel, onConfirm }) {
  const [remark, setRemark] = useState('');
  const QUICK = ['Travelling between sites', 'Missed punch earlier', 'Stuck in traffic', 'Site visit / meeting', 'Other (typed below)'];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur flex items-end" data-testid="off-radius-sheet">
      <div className="w-full bg-white text-slate-900 rounded-t-3xl px-6 pt-6 pb-9 border-t-4 border-amber-500 max-h-[90vh] overflow-y-auto">
        <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-3 text-2xl">⚠</div>
        <h3 className="text-center font-display font-bold text-xl">You're outside the site geofence</h3>
        <p className="text-center text-sm text-slate-600 mt-1">
          {pending.distance_m} m from project location (allowed {pending.radius_m} m). Please tell us why so HR can review.
        </p>
        <div className="mt-4 space-y-1.5">
          {QUICK.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setRemark(q === 'Other (typed below)' ? '' : q)}
              className={`w-full text-left text-sm px-3 py-2 rounded-md border transition ${
                remark === q ? 'border-[#1F7A6E] bg-[#1F7A6E]/5 text-[#1F7A6E] font-semibold' : 'border-slate-200 text-slate-700'
              }`}
              data-testid={`off-radius-quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0,30)}`}
            >{q}</button>
          ))}
        </div>
        <textarea
          rows={3}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Type a clear, honest reason (required)"
          className="mt-3 w-full border border-slate-200 rounded-md p-3 text-sm focus:border-[#1F7A6E] focus:outline-none"
          data-testid="off-radius-remark-input"
        />
        <div className="mt-4 flex gap-2">
          <button onClick={onCancel} className="flex-1 bg-white border-2 border-slate-200 py-3 rounded-full font-display font-bold text-sm" data-testid="off-radius-cancel">Cancel</button>
          <button
            onClick={() => onConfirm(remark.trim())}
            disabled={busy || !remark.trim()}
            className="flex-1 bg-[#1F7A6E] text-white py-3 rounded-full font-display font-bold text-sm disabled:opacity-40"
            data-testid="off-radius-confirm"
          >{busy ? 'Submitting…' : 'Submit Punch'}</button>
        </div>
      </div>
    </div>
  );
}
